import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { HelpCircle, CheckCircle2, AlertTriangle, Shield } from 'lucide-react';

interface SecurityQuestionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasQuestions: boolean;
  onStatusChange: () => void;
}

const SECURITY_QUESTIONS = [
  "What was the name of your first pet?",
  "What is your mother's maiden name?",
  "What city were you born in?",
  "What was the name of your elementary school?",
  "What is your favorite book?",
  "What was your childhood nickname?",
  "What is the name of the street you grew up on?",
  "What was your first car?",
  "What is your favorite movie?",
  "In what city did you meet your spouse/partner?",
  "What was your favorite food as a child?",
  "What is the name of your favorite childhood friend?",
];

export function SecurityQuestionsModal({ 
  open, 
  onOpenChange, 
  hasQuestions, 
  onStatusChange 
}: SecurityQuestionsModalProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState({
    question1: '',
    answer1: '',
    question2: '',
    answer2: '',
    question3: '',
    answer3: '',
  });

  const hashAnswer = async (answer: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(answer.toLowerCase().trim());
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const handleSave = async () => {
    if (!user) return;

    // Validate all fields are filled
    if (!questions.question1 || !questions.answer1 || 
        !questions.question2 || !questions.answer2 || 
        !questions.question3 || !questions.answer3) {
      toast({
        title: "Incomplete form",
        description: "Please answer all three security questions.",
        variant: "destructive",
      });
      return;
    }

    // Validate all questions are different
    if (questions.question1 === questions.question2 || 
        questions.question1 === questions.question3 || 
        questions.question2 === questions.question3) {
      toast({
        title: "Duplicate questions",
        description: "Please select three different security questions.",
        variant: "destructive",
      });
      return;
    }

    // Validate answers are not too short
    if (questions.answer1.trim().length < 2 || 
        questions.answer2.trim().length < 2 || 
        questions.answer3.trim().length < 2) {
      toast({
        title: "Invalid answers",
        description: "Answers must be at least 2 characters long.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Hash all answers
      const [hash1, hash2, hash3] = await Promise.all([
        hashAnswer(questions.answer1),
        hashAnswer(questions.answer2),
        hashAnswer(questions.answer3),
      ]);

      // Upsert security questions
      const { error } = await supabase
        .from('security_questions')
        .upsert({
          user_id: user.id,
          question_1: questions.question1,
          answer_1_hash: hash1,
          question_2: questions.question2,
          answer_2_hash: hash2,
          question_3: questions.question3,
          answer_3_hash: hash3,
        });

      if (error) throw error;

      toast({
        title: "Security questions saved",
        description: "Your security questions have been set up successfully.",
      });

      onStatusChange();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving security questions:', error);
      toast({
        title: "Save failed",
        description: "Failed to save security questions. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!user) return;

    const confirmed = window.confirm(
      "Are you sure you want to delete your security questions? This will remove a recovery option for your account."
    );

    if (!confirmed) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('security_questions')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: "Security questions deleted",
        description: "Your security questions have been removed.",
      });

      onStatusChange();
      onOpenChange(false);
    } catch (error) {
      console.error('Error deleting security questions:', error);
      toast({
        title: "Delete failed",
        description: "Failed to delete security questions. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5" />
            Security Questions
          </DialogTitle>
          <DialogDescription>
            Set up security questions as an additional recovery method for your account
          </DialogDescription>
        </DialogHeader>

        {!hasQuestions ? (
          <div className="space-y-6">
            <Alert className="border-blue-500/50 bg-blue-500/10">
              <Shield className="h-4 w-4 text-blue-500" />
              <AlertDescription className="text-sm">
                Security questions provide an alternative way to verify your identity 
                if you lose access to your 2FA device or recovery codes.
              </AlertDescription>
            </Alert>

            <div className="space-y-6">
              {/* Question 1 */}
              <div className="space-y-3 p-4 border rounded-lg bg-card/50">
                <Label className="text-sm font-semibold">Security Question 1</Label>
                <Select
                  value={questions.question1}
                  onValueChange={(value) => setQuestions({ ...questions, question1: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a question" />
                  </SelectTrigger>
                  <SelectContent>
                    {SECURITY_QUESTIONS.map((q) => (
                      <SelectItem key={q} value={q}>
                        {q}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="text"
                  placeholder="Your answer"
                  value={questions.answer1}
                  onChange={(e) => setQuestions({ ...questions, answer1: e.target.value })}
                />
              </div>

              {/* Question 2 */}
              <div className="space-y-3 p-4 border rounded-lg bg-card/50">
                <Label className="text-sm font-semibold">Security Question 2</Label>
                <Select
                  value={questions.question2}
                  onValueChange={(value) => setQuestions({ ...questions, question2: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a question" />
                  </SelectTrigger>
                  <SelectContent>
                    {SECURITY_QUESTIONS.map((q) => (
                      <SelectItem key={q} value={q}>
                        {q}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="text"
                  placeholder="Your answer"
                  value={questions.answer2}
                  onChange={(e) => setQuestions({ ...questions, answer2: e.target.value })}
                />
              </div>

              {/* Question 3 */}
              <div className="space-y-3 p-4 border rounded-lg bg-card/50">
                <Label className="text-sm font-semibold">Security Question 3</Label>
                <Select
                  value={questions.question3}
                  onValueChange={(value) => setQuestions({ ...questions, question3: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a question" />
                  </SelectTrigger>
                  <SelectContent>
                    {SECURITY_QUESTIONS.map((q) => (
                      <SelectItem key={q} value={q}>
                        {q}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="text"
                  placeholder="Your answer"
                  value={questions.answer3}
                  onChange={(e) => setQuestions({ ...questions, answer3: e.target.value })}
                />
              </div>
            </div>

            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                <strong>Important:</strong> Choose questions with answers you'll remember. 
                Answers are case-insensitive and will be hashed for security.
              </AlertDescription>
            </Alert>

            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={loading} className="flex-1">
                {loading ? 'Saving...' : 'Save Security Questions'}
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-center p-6 bg-green-500/10 rounded-lg border border-green-500/20">
              <div className="text-center space-y-2">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
                <p className="font-medium">Security Questions Active</p>
                <p className="text-sm text-muted-foreground">
                  You have set up 3 security questions for account recovery
                </p>
              </div>
            </div>

            <Alert className="border-amber-500/50 bg-amber-500/10">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <AlertDescription className="text-sm">
                If you want to change your security questions, you'll need to delete 
                the current ones and set them up again.
              </AlertDescription>
            </Alert>

            <div className="flex gap-2">
              <Button 
                variant="destructive" 
                onClick={handleDelete} 
                disabled={loading}
                className="flex-1"
              >
                {loading ? 'Deleting...' : 'Delete Security Questions'}
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
